/**
 * WebSocket close codes the server sends (4000-4999 are application codes), and what the client
 * does after each. Browsers report a connection that dropped without a close frame as 1006; servers
 * never send that one.
 */
export const CloseCode = {
  normal: 1000,
  goingAway: 1001,
  internalError: 1011,
  serviceRestart: 1012,
  tryAgainLater: 1013,
  /** The first message wasn't a valid hello, or the client broke the protocol. A client bug. */
  protocolError: 4000,
  /** The client's protocol is too old: reload the page to get the new bundle. Frozen. */
  reloadRequired: 4001,
  /** The client is newer than the server (a deploy is rolling out): reconnect after a short wait. Frozen. */
  retryLater: 4002,
  /** Missing or expired room token: get a fresh one from the API, then reconnect. */
  unauthorized: 4003,
  /** The same seat connected again from another tab or device. */
  replaced: 4004,
  roomFull: 4005,
  /** The room expired or was closed. */
  roomClosed: 4006,
  /** Removed by the host (safety). */
  kicked: 4007,
  /** Rate limit or abuse. */
  policy: 4008,
} as const;

export type CloseAction = "reconnect" | "reload" | "stop";

/**
 * What the client does after a close. Unknown 4xxx codes mean stop, so a newer server can never cause
 * a reconnect storm in an older client. Anything else (1006, network trouble) means reconnect.
 */
export function closeAction(code: number): CloseAction {
  switch (code) {
    case CloseCode.reloadRequired:
      return "reload";
    case CloseCode.retryLater:
    case CloseCode.unauthorized:
      return "reconnect";
    case CloseCode.normal:
      return "stop";
    default:
      return code >= 4000 && code <= 4999 ? "stop" : "reconnect";
  }
}
